/**
 * 재무·인사 모듈 내비게이션 — 단일 출처(single source of truth).
 *
 * ★ 섹션·탭 이름은 여기 한 곳에서만 고치면 된다.
 *   → 사이드바 트리, 페이지 상단 탭, 통합 검색 결과에 전부 자동 반영된다.
 *   (예전처럼 "사이드바 라벨"과 "페이지 탭 라벨"을 두 군데서 따로 고칠 필요 없음)
 *
 * 필드 의미:
 * - key   : URL·코드용 안정 식별자. label 을 바꿔도 이 값은 유지 → 링크·컴포넌트 매핑이 안 깨진다.
 * - label : 화면 표시 이름 (사이드바·탭·검색 공통). ★ 이름 변경은 이 값만 수정.
 * - perm  : 열람 권한 코드. 권한 없는 탭은 사이드바·탭·검색에서 자동 숨김.
 * - subtitle : 페이지 상단 부제목 (섹션 단위).
 */

export const FINANCE_NAV = {
    path: "/finance",
    moduleLabel: "재무",
    sections: [
        {
            key: "view", label: "자금 현황",
            subtitle: "요약·현금흐름표·부서별 현황·프로젝트 손익·자금일보를 확인합니다",
            tabs: [
                { key: "dashboard",   label: "요약",       perm: "FINANCE_DASHBOARD" },
                { key: "cashflow",    label: "현금 흐름표",     perm: "FINANCE_CASHFLOW" },
                { key: "expensereport", label: "지출 예정 보고서", perm: "FINANCE_CASHFLOW" },
                { key: "urgentexpense", label: "긴급 지출 보고서", perm: "FINANCE_CASHFLOW" },
                { key: "fixedcalendar", label: "고정비 달력",   perm: "FINANCE_CASHFLOW" },
                { key: "cashcalendar",  label: "자금 달력",     perm: "FINANCE_CASHFLOW" },
                { key: "deptsummary", label: "부서별 매출/지출",    perm: "FINANCE_DASHBOARD" },
                { key: "txlist",      label: "입/출금 목록", perm: "FINANCE_TRANSACTION" },
                { key: "project",     label: "프로젝트별 손익", perm: "FINANCE_PROJECT" },
                { key: "dailycash",   label: "자금일보/계좌 관리",       perm: "FINANCE_DAILYCASH" },
            ],
        },
        {
            key: "register", label: "자금 관리",
            subtitle: "미수금·거래처·예산을 관리합니다 (수입/지출·자금계획 등록은 자금 현황에서 바로)",
            tabs: [
                // 수입/지출 등록 → '수입/지출 목록'에, 자금계획 등록 → '현금 흐름표'에 통합됨
                { key: "receivable", label: "미수금 관리",    perm: "FINANCE_RECEIVABLE" },
                { key: "vendor",     label: "거래처 관리",    perm: "FINANCE_VENDOR" },
                { key: "budget",     label: "부서 예산 계획",      perm: "FINANCE_BUDGET" },
            ],
        },

        {
            key: "approval", label: "지출확정",
            subtitle: "결재 승인 건의 재무 확정(지출확정·보류)을 처리합니다",
            tabs: [
                { key: "approvallink", label: "결재 연동 내역", perm: "FINANCE_APPROVAL" },
            ],
        },

        {
            // 지출요청(부서) — 결재(대금지급요청·구매품의서)를 부서별로 모아 지출 예정/확정 내역을 조회한다.
            // 사이드바 하위는 부서 목록(동적)으로 펼쳐진다 → OnandmeSidebar 에서 부서 링크를 주입.
            key: "expenserequest", label: "지출요청(부서)",
            subtitle: "부서별 지출요청(대금지급요청·구매품의서) 결재의 지출 예정·확정 내역을 조회합니다",
            tabs: [
                { key: "expenserequest", label: "지출요청", perm: "FINANCE_APPROVAL" },
            ],
        },

        {
            // 지출요청(프로젝트별) — 같은 결재를 프로젝트(채널) 기준으로 묶는다.
            //
            // 부서 쪽과 달리 사이드바를 프로젝트 목록으로 펼치지 않는다. 부서는 열댓 개로 고정이지만
            // 프로젝트는 계속 늘어나서(종료된 것까지 남는다) 사이드바가 통째로 프로젝트 목록이 된다.
            // 그래서 항목은 하나만 두고, 화면 안 드롭다운·검색으로 찾게 한다
            // → OnandmeSidebar 에서 children 을 비워 단일 링크로 만든다.
            key: "expenserequestproject", label: "지출요청(프로젝트별)",
            subtitle: "프로젝트별 지출요청(대금지급요청·구매품의서) 결재의 지출 예정·확정 내역을 조회합니다",
            tabs: [
                { key: "expenserequestproject", label: "지출요청(프로젝트별)", perm: "FINANCE_APPROVAL" },
            ],
        },
    ],
};

export const HR_NAV = {
    path: "/hr",
    moduleLabel: "인사",
    sections: [
        {
            key: "leave", label: "근태관리",
            subtitle: "휴가·연차 현황과 근태 기록을 관리합니다",
            tabs: [
                { key: "leave",      label: "휴가 현황", perm: "HR_LEAVE" },
                { key: "attendance", label: "근태 관리", perm: "HR_ATTENDANCE" },
            ],
        },
        {
            key: "approval", label: "결재연동",
            subtitle: "휴가·초과근무 등 HR 관련 결재 연동을 처리합니다",
            tabs: [
                { key: "approval", label: "결재 연동", perm: "HR_APPROVAL" },
            ],
        },
        {
            key: "employee", label: "인사관리",
            subtitle: "직원 현황·급여·외부 열람용 임시계정을 관리합니다",
            tabs: [
                { key: "employee", label: "직원 관리", perm: "HR_EMPLOYEE" },
                { key: "salary",   label: "급여 관리", perm: "HR_SALARY" },
                { key: "temp",     label: "키비쥬얼 확인 외부 임시계정",  perm: "HR_TEMP_ACCOUNT" },
            ],
        },
        {
            key: "org", label: "조직관리",
            subtitle: "부서·직위(직급)·직책 체계를 관리합니다",
            tabs: [
                { key: "dept",     label: "부서 관리", perm: "HR_DEPT" },
                { key: "position", label: "직위/직급 관리", perm: "HR_POSITION" },
                { key: "duty",     label: "직책 관리", perm: "HR_DUTY" },
            ],
        },
    ],
};

export const ALL_NAVS = [FINANCE_NAV, HR_NAV];

const enc = encodeURIComponent;

/** 섹션 URL (탭 미지정 — 페이지가 첫 탭으로 열고 URL 을 동기화). */
export const sectionUrl = (nav, sectionKey) => `${nav.path}?section=${sectionKey}`;

/** 탭 딥링크 URL — 사이드바 탭 항목·검색·페이지 탭 이동이 공통으로 쓴다. */
export const tabUrl = (nav, sectionKey, label) => `${nav.path}?section=${sectionKey}&tab=${enc(label)}`;

/**
 * 사이드바 트리 children 생성 — 섹션(링크) > 탭(링크) 2계층.
 * canSee(perm) 로 권한 필터: 볼 수 있는 탭이 하나도 없는 섹션은 통째로 숨긴다.
 */
export function buildModuleChildren(nav, canSee) {
    return nav.sections
        .map(s => {
            const tabs = s.tabs.filter(t => canSee(t.perm));
            if (tabs.length === 0) return null;
            const sid = `${nav.path}${s.key}`.replace(/\W/g, "");
            return {
                id: sid,
                label: s.label,
                to: sectionUrl(nav, s.key),
                children: tabs.map(t => ({
                    id: `${sid}${t.key}`.replace(/\W/g, ""),
                    label: t.label,
                    isTab: true,
                    to: tabUrl(nav, s.key, t.label),
                })),
            };
        })
        .filter(Boolean);
}
